import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { LLMVariableType, SlimLLMVariable } from "models/LLMs/LLMVariable";
import { observer } from "mobx-react";

export interface LLMVariableValueOption {
    label: string;
    value: string;
}

/**
 * How a variable's value should be entered: picked from a fixed set, typed
 * freely, or not at all (presence only)
 */
export type LLMVariableValueControl =
    | { type: "select"; options: LLMVariableValueOption[] }
    | { type: "freeText" }
    | { type: "unsupported" };

const booleanOptions: LLMVariableValueOption[] = [
    { label: "True", value: "True" },
    { label: "False", value: "False" },
];

/**
 * Resolves the values a variable can be compared against. This is the rule the
 * metadata filters on the Volume Control step have always used, kept in one
 * place so every surface that compares a variable offers the same values.
 */
export function getLLMVariableValueControl(
    variable?: SlimLLMVariable,
): LLMVariableValueControl {
    // An unknown variable, e.g. extended metadata, is compared as free text.
    if (!variable) return { type: "freeText" };

    // Booleans ignore whatever values the variable declares.
    if (variable.type === LLMVariableType.Boolean)
        return { type: "select", options: booleanOptions };

    const values = variable.values;
    if (!values || values.length === 0) return { type: "freeText" };

    const hasObjects = values.some((value) => typeof value === "object");
    const hasPrimitives = values.some((value) => typeof value !== "object");

    if (hasObjects && hasPrimitives) {
        console.error("Variable values are not of same type");
        return { type: "unsupported" };
    }

    if (hasObjects) {
        const asObjects = values as Record<string, unknown>[];

        if (!asObjects.every((value) => "label" in value && "value" in value)) {
            console.error(
                "Invalid variable values. If values is an array of objects all entries must have a label and value property.",
            );
            return { type: "unsupported" };
        }

        return {
            type: "select",
            options: asObjects.map((value) => ({
                label: String(value.label),
                value: String(value.value),
            })),
        };
    }

    return {
        type: "select",
        options: values.map((value) => ({
            label: String(value),
            value: String(value),
        })),
    };
}

/** The selectable values for a variable, empty when it is compared as free text. */
export function getLLMVariableValueOptions(
    variable?: SlimLLMVariable,
): LLMVariableValueOption[] {
    const control = getLLMVariableValueControl(variable);

    return control.type === "select" ? control.options : [];
}

interface LLMVariableValueFieldProps {
    id: string;
    variable?: SlimLLMVariable;
    value?: string;
    onChange: (value: string) => void;
    fullWidth?: boolean;
    error?: boolean;
    hideErrorIcon?: boolean;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Input for the value side of a comparison against an LLM executor variable.
 */
export const LLMVariableValueField = observer(function (
    props: LLMVariableValueFieldProps,
) {
    const control = getLLMVariableValueControl(props.variable);

    if (control.type === "unsupported") return null;

    if (control.type === "freeText") {
        return (
            <AcxMainTextField
                id={props.id}
                fullWidth={props.fullWidth}
                placeholderText={props.placeholder ?? "Enter value..."}
                error={props.error}
                hideErrorIcon={props.hideErrorIcon}
                isDisabled={props.disabled}
                value={props.value ?? ""}
                onChange={(event) => props.onChange(event.target.value)}
            />
        );
    }

    const isBoolean = props.variable?.type === LLMVariableType.Boolean;
    const selected =
        control.options.find((option) => option.value === props.value) ??
        // A stored value the variable no longer offers is still shown rather than
        // silently reading as empty, and a boolean with nothing stored reads as
        // False. Both are how the metadata filters have always behaved.
        (props.value
            ? { label: props.value, value: props.value }
            : isBoolean
            ? control.options.find((option) => option.value === "False")
            : undefined);

    return (
        <AcxSelectSingle
            id={props.id}
            fullWidth={props.fullWidth}
            labelField="label"
            valueField="value"
            options={control.options}
            placeholder={props.placeholder}
            error={props.error}
            isDisabled={props.disabled}
            defaultValue={selected}
            onChange={(option) => props.onChange(option.value)}
        />
    );
});
